import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ProcessedTicketDetail } from '@/types/knowledge';
import { AlertCircle, ExternalLink, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ProcessedTicketsTableProps {
  tickets: ProcessedTicketDetail[];
  onViewError: (ticket: ProcessedTicketDetail) => void;
  onViewConversation: (ticket: ProcessedTicketDetail) => void;
  isLoading?: boolean;
}

function getStatusLabel(status: ProcessedTicketDetail['status']): string {
  if (status === 'created') return 'Criado';
  if (status === 'updated') return 'Atualizado';
  if (status === 'error') return 'Erro';
  return 'Skipped';
}

function getStatusVariant(status: ProcessedTicketDetail['status']) {
  if (status === 'error') return 'destructive' as const;
  if (status === 'skipped') return 'secondary' as const;
  return 'default' as const;
}

function getSkipReasonLabel(skipReason?: string): string {
  if (!skipReason) return 'Motivo não registrado';
  if (skipReason === 'duplicate_protocol') return 'Protocolo duplicado';
  if (skipReason === 'insufficient_messages') return 'Mensagens insuficientes';
  if (skipReason === 'no_human_interaction') return 'Sem interacao humana';
  if (skipReason === 'bot_only_conversation') return 'Conversa apenas com bot';
  if (skipReason.startsWith('not_support_conversation')) return 'Conversa nao relacionada a suporte';
  return skipReason;
}

function canOpenConversation(ticket: ProcessedTicketDetail): boolean {
  if (ticket.status === 'created' || ticket.status === 'updated') {
    return true;
  }

  if (ticket.status === 'skipped') {
    return ticket.skipReason !== 'duplicate_protocol';
  }

  return false;
}

export function ProcessedTicketsTable({
  tickets,
  onViewError,
  onViewConversation,
  isLoading = false,
}: ProcessedTicketsTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="hidden md:block rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Protocolo</TableHead>
                <TableHead>Ticket ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Documento</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell className="text-right"><Skeleton className="h-8 w-24 ml-auto" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="md:hidden space-y-3">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="p-4 space-y-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-3 w-40" />
              <Skeleton className="h-8 w-24" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground">
        Nenhum ticket processado nesta extração
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="hidden md:block rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Protocolo</TableHead>
              <TableHead>Ticket ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Documento</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tickets.map((ticket) => (
              <TableRow key={`${ticket.protocol}-${ticket.ticketId}`} className="hover:bg-muted/30">
                <TableCell className="font-mono text-xs">{ticket.protocol}</TableCell>
                <TableCell className="font-mono text-xs">{ticket.ticketId}</TableCell>
                <TableCell>
                  <Badge variant={getStatusVariant(ticket.status)}>
                    {getStatusLabel(ticket.status)}
                  </Badge>
                  {ticket.status === 'skipped' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {getSkipReasonLabel(ticket.skipReason)}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  {ticket.documentId ? (
                    <Link
                      to={`/knowledge/${ticket.documentId}`}
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {ticket.documentId}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right space-x-2">
                  {ticket.status === 'error' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewError(ticket)}
                      aria-label={`Ver detalhes do erro do protocolo ${ticket.protocol}`}
                    >
                      <AlertCircle className="h-4 w-4 mr-1" />
                      Erro
                    </Button>
                  )}
                  {canOpenConversation(ticket) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewConversation(ticket)}
                      aria-label={`Ver conversa do protocolo ${ticket.protocol}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Conversa
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="md:hidden space-y-3">
        {tickets.map((ticket) => (
          <Card key={`${ticket.protocol}-${ticket.ticketId}`} className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-xs">{ticket.protocol}</span>
              <Badge variant={getStatusVariant(ticket.status)}>
                {getStatusLabel(ticket.status)}
              </Badge>
            </div>
            {ticket.status === 'skipped' && (
              <p className="text-xs text-muted-foreground">
                {getSkipReasonLabel(ticket.skipReason)}
              </p>
            )}
            <div className="text-xs text-muted-foreground font-mono">
              Ticket: {ticket.ticketId}
            </div>
            <div className="flex gap-2">
              {ticket.status === 'error' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewError(ticket)}
                  aria-label={`Ver detalhes do erro do protocolo ${ticket.protocol}`}
                >
                  Ver erro
                </Button>
              )}
              {canOpenConversation(ticket) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewConversation(ticket)}
                  aria-label={`Ver conversa do protocolo ${ticket.protocol}`}
                >
                  Ver conversa
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
